import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ContentPlanRootState as S } from '../../elements/contentPlan/store';
import { ElementAction } from '../../elements/elementAction';
import { Functions } from '../../types/types';
import { Schema, SchemaField, Workspace } from '../../types/Metadata';
import { SchemaModalState, updateSchemaModalState } from './schemaModalReducer';
import { Pages } from './schemaModal';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { setLoading } from '../Loader/loaderAction';
import { clearAlert, setError } from '../Alert/alertAction';
import {
  reloadWorkspace,
  selectWorkspace
} from '../ContentPlanning/contentPlanningActions';
import { DataType } from '../../enums/DataType';
import { clone, findOneByProps, getSchema } from '../../utils';
import { NodeType } from '../../enums/NodeType';
import { updatePlanningState } from '../ContentPlanning/contentPlanningReducer';
import URLManager from '../../utils/urlManager';
import { updateSelectedView } from '../View/viewReducer';
import { FieldType } from '../../enums/FieldType';
import { FieldRequiredValue } from '../../enums/FieldRequiredValue';
import { FieldShowValue } from '../../enums/FieldShowValue';
import { ItemShowOptions, RequiredOptions } from './FieldCard/fieldCard';

enum Page {
  Setup = 'Setup'
}

const save =
  (updateUrl: URLManager.UpdateURLFn): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { schemaModal, planning, view } = getState();
    const workspaceRefId = planning.selectedWorkspace.props.refId;
    const {
      refId,
      nodeType,
      name,
      description,
      fields = [],
      displayField,
      schemaForms
    } = schemaModal;
    const { updateSchema, createSchema, getWorkspace } = getAPI();
    try {
      const { selectedWorkspace } = planning;
      const schemas = selectedWorkspace?.schemas;
      const type = nodeType;
      const schemaToBeSaved = getSchema(schemas, nodeType);
      const schema = {
        schemaForms,
        props: {
          refId,
          type,
          name,
          displayField,
          description,
          fields: fields.map(f => {
            const baseSchemaField =
              schemaToBeSaved?.baseSchema?.props?.fields?.filter(
                bf => bf?.field == f?.field
              )[0];
            let editableCriteria = f.editableCriteria;
            if (baseSchemaField?.editableCriteria?.length) {
              editableCriteria = null;
              f.editableCriteria = editableCriteria;
            }
            if (f.isSystemField) {
              return {
                field: f.field,
                isOrderField: f.isOrderField,
                requiredAt: f.requiredAt,
                showAt: f.showAt,
                editableCriteria,
                isSystemField: true,
                name:
                  !!f.name?.length && f.name != baseSchemaField?.name
                    ? f.name
                    : undefined,
                description:
                  !!f.description?.length &&
                  f.description != baseSchemaField?.description
                    ? f.description
                    : undefined,
                treeGroupName:
                  !!f.treeGroupName?.length &&
                  f.treeGroupName != baseSchemaField?.treeGroupName
                    ? f.treeGroupName
                    : undefined
              };
            }
            if (f.isInherited) {
              return {
                field: f.field,
                isOrderField: f.isOrderField,
                requiredAt: f.requiredAt,
                editableCriteria,
                showAt: f.showAt,
                name:
                  !!f.name?.length && f.name != baseSchemaField?.name
                    ? f.name
                    : undefined,
                description:
                  !!f.description?.length &&
                  f.description != baseSchemaField?.description
                    ? f.description
                    : undefined,
                isInherited: true,
                treeGroupName:
                  !!f.treeGroupName?.length &&
                  f.treeGroupName != baseSchemaField?.treeGroupName
                    ? f.treeGroupName
                    : undefined
              };
            }
            const { key, displayName, ...field } = f;
            return field;
          })
        }
      } as Schema;

      dispatch(setLoading(true, 'schemaModalLoader'));
      const upsertFn = nodeType ? updateSchema : createSchema;
      const results = await upsertFn({ workspaceRefId, schema });
      if (results?.length) {
        const viewRefId = results[0]?.props?.refId;
        if (view.selected?.props?.refId == viewRefId) {
          dispatch(updateSelectedView(null));
          dispatch(reloadWorkspace(workspaceRefId, viewRefId));
        } else if (viewRefId) {
          const workspace = await getWorkspace(workspaceRefId);
          dispatch(updatePlanningState({ selectedWorkspace: workspace }));
          updateUrl({ w: workspaceRefId, v: viewRefId });
          //dispatch(selectWorkspace(workspaceRefId, viewRefId));
        } else {
          dispatch(reloadWorkspace(workspaceRefId));
        }
        dispatch(closeSchemaModal());
      } else {
        throw new Error(
          `Failed to upsert data type, please contact CPP Help if this issue persists.`
        );
      }
    } catch (err) {
      console.error(
        'An unexpected error occurred while saving schema modal',
        err
      );
      dispatch(setError(err.message, 'schemaModalAlert'));
      dispatch(updateSchemaModalState({ additionalInfo: err?.additionalInfo }));
    } finally {
      dispatch(setLoading(false, 'schemaModalLoader'));
    }
  };

const deleteSchema =
  (schema: Schema): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { deleteSchema, getWorkspace } = getAPI();
    const { planning, view } = getState();
    const workspaceRefId = planning?.selectedWorkspace?.props?.refId;
    const dataType = view?.selected?.card?.props?.data;
    if (!workspaceRefId) throw new Error('Invalid selected workspace id');
    const results = await deleteSchema({
      refId: schema.props.refId,
      type: schema.props.type,
      workspaceRefId
    });
    if (results?.length) {
      const workspace = await getWorkspace(workspaceRefId);
      dispatch(updatePlanningState({ selectedWorkspace: workspace }));
      if (dataType == schema.props.type) {
        dispatch(selectWorkspace(workspaceRefId));
      }
    } else {
      throw new Error(
        `Failed to delete data type, please contact CPP Help if this issue persists.`
      );
    }
  };

const openSchemaModal =
  (s?: Schema): ElementAction<Functions, S> =>
  dispatch => {
    if (s?.props?.type && s?.props?.refId) {
      const schema = clone(s);
      dispatch(
        updateSchemaModalState({
          page: Page.Setup,
          refId: schema.props.refId,
          nodeType: schema.props.type,
          name: schema.props.name,
          description: schema.props.description,
          displayField: schema.props.displayField,
          fields: clone(schema.props.fields),
          schemaForms: clone(schema?.schemaForms)
        })
      );
    } else {
      dispatch(
        updateSchemaModalState({
          page: Page.Setup,
          fields: [
            {
              field: 'isActive',
              dataType: DataType.boolean,
              name: 'Active',
              requiredAt: FieldRequiredValue.NotRequired
            },
            {
              field: 'createdBy',
              dataType: NodeType.Employee,
              name: 'Created By/On',
              relationType: 'CREATED_BY',
              fieldType: FieldType.reference,
              requiredAt: FieldRequiredValue.NotRequired,
              readOnly: true,
              showAt: FieldShowValue.Show,
              excludeFromView: true
            },
            {
              field: 'modifiedBy',
              dataType: NodeType.Employee,
              name: 'Last Modified By/On',
              relationType: 'MODIFIED_BY',
              fieldType: FieldType.reference,
              requiredAt: FieldRequiredValue.NotRequired,
              readOnly: true,
              showAt: FieldShowValue.Show,
              excludeFromView: true
            }
          ]
        })
      );
    }
  };

const closeSchemaModal = (): ElementAction<Functions, S> => dispatch => {
  dispatch(updateSchemaModalState({ page: null }));
  dispatch(clearAlert('schemaModalAlert'));
};

const goToPage =
  (p: string | 1 | -1): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const { page } = getState().schemaModal;
    if (typeof p == 'number') {
      if (page) {
        const idx = Pages.map(_ => _.label).indexOf(page);
        p = Pages[idx + p].label;
      } else p = null;
    }
    if (p) {
      dispatch(updateSchemaModalState({ page: p as Page }));
    }
  };

type VS = Partial<Omit<SchemaModalState, 'page'>>;
const setSchemaSetting =
  (s: VS): ElementAction<Functions, S> =>
  dispatch => {
    dispatch(updateSchemaModalState(s));
  };

const randomFieldKey = (fields: SchemaField[]) => {
  let field = 'p' + Math.random().toString(36).substring(5);
  let f = findOne(fields, { field });
  if (f == null) return field;
  return randomFieldKey(fields);
};

const addField = (): ElementAction<Functions, S> => (dispatch, getState) => {
  const { schemaModal, planning } = getState();
  let fields = schemaModal?.fields;
  if (!fields) {
    fields = [];
  }
  const newField = makeNewField(
    schemaModal?.nodeType,
    fields,
    planning.selectedWorkspace
  );
  dispatch(
    updateSchemaModalState({
      fields: addNewField(fields, newField),
      selectedField: newField.field
    })
  );
};

const addNewField = (list: SchemaField[], newField: SchemaField) => {
  const defaultFields = ['isActive', 'createdBy', 'modifiedBy'];
  let insertIndex = 0;
  for (let i = list.length - 1; i >= 0; i--) {
    if (!defaultFields.includes(list[i]?.field)) {
      insertIndex = i + 1;
      break;
    }
  }
  list.splice(insertIndex, 0, newField);
  return list.filter(x => x);
};

const makeNewField = (
  type: NodeType,
  fields: SchemaField[],
  workspace: Workspace
) => {
  const field: SchemaField = {
    field: randomFieldKey(fields),
    dataType: DataType.string,
    fieldType: FieldType.static,
    requiredAt: FieldRequiredValue.NotRequired
  };
  if (!workspace?.labels?.includes(NodeType.AdminWorkspace)) {
    return field;
  }
  if (type == NodeType.Item) {
    field.showAt = FieldShowValue.AlwaysHide;
  }
  return field;
};

export const addItemField =
  (
    destinationIndex: number,
    aboveOrBelow: 'above' | 'below',
    filteredFields: SchemaField[]
  ): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const { schemaModal, planning } = getState();
    let fields = schemaModal?.fields;
    if (!fields) fields = [];
    const destinationField = filteredFields[destinationIndex];
    let actualDestinationIndex = fields.findIndex(
      field => JSON.stringify(field) === JSON.stringify(destinationField)
    );
    if (aboveOrBelow == 'below') actualDestinationIndex += 1;
    const newField = makeNewField(
      schemaModal?.nodeType,
      fields,
      planning.selectedWorkspace
    );
    fields.splice(actualDestinationIndex, 0, newField);
    fields.filter(x => x);
    dispatch(
      updateSchemaModalState({
        fields: fields,
        selectedField: newField.field
      })
    );
  };

const removeField =
  (field: string): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const { fields, schemaForms } = getState().schemaModal;
    schemaForms?.forEach(sf => {
      sf.props.fields = sf.props.fields.filter(f => f.field != field);
    });
    dispatch(
      updateSchemaModalState({
        schemaForms,
        fields: fields.reduce((p, f) => {
          if (f.field != field) p.push(f);
          else if (f.isSystemField || f.isInherited) {
            p.push(f);
          }
          return p;
        }, [])
      })
    );
  };

type FieldInfo = Partial<
  Omit<SchemaField, 'field' | 'displayName' | 'key' | 'dataSchema'>
>;

const updateField =
  (
    field: string,
    fieldInfo: FieldInfo,
    form: Schema
  ): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    if (form?.props?.itemType) {
      const schemaForm = findOneByProps(getState().schemaModal.schemaForms, {
        itemType: form.props.itemType
      });
      if (schemaForm) {
        schemaForm.props.fields = schemaForm.props.fields.map(f =>
          f.field == field ? { ...f, ...fieldInfo } : f
        );
        dispatch(updateSchemaForm(schemaForm));
      }
      return;
    }
    let fields = getState().schemaModal?.fields;
    if (fieldInfo.isOrderField) {
      fields.forEach(f => {
        if (f.isOrderField) delete f.isOrderField;
      });
    }
    dispatch(
      updateSchemaModalState({
        fields: fields.map(f => (f.field == field ? { ...f, ...fieldInfo } : f))
      })
    );
  };

const handleShowInOnChange =
  (
    selectedField: string,
    itemTypes: string[],
    treeFields: SchemaField[]
  ): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const schemaForms = getState().schemaModal.schemaForms || [];
    itemTypes.forEach(itemType => {
      let schemaForm = schemaForms.filter(
        sf => sf.props.itemType == itemType
      )[0];
      if (!schemaForm) {
        schemaForm = {
          labels: [NodeType.SchemaForm],
          props: {
            itemType,
            fields: []
          }
        } as Schema;
        schemaForms.push(schemaForm);
      }
    });

    const updateSchemaForms = (field: string, forms: Schema[]) =>
      forms.map(schemaForm => {
        const { fields, itemType } = schemaForm.props;
        const fieldExists = findOne(fields, { field });
        if (itemTypes.includes(itemType)) {
          if (!fieldExists) {
            return {
              ...schemaForm,
              props: {
                ...schemaForm.props,
                fields: [
                  ...fields,
                  {
                    field,
                    requiredAt: FieldRequiredValue.NotRequired,
                    showAt: FieldShowValue.Hide
                  }
                ]
              }
            };
          }
        } else {
          if (fieldExists) {
            return {
              ...schemaForm,
              props: {
                ...schemaForm.props,
                fields: fields.filter(f => f.field != field)
              }
            };
          }
        }
        return schemaForm;
      });
    if (!treeFields) {
      const updatedSchemaForms = updateSchemaForms(selectedField, schemaForms);
      dispatch(updateSchemaModalState({ schemaForms: updatedSchemaForms }));
    } else {
      const fields = treeFields.map(f => f.field);
      if (fields[0] == selectedField) {
        let updatedSchemaForms = updateSchemaForms(selectedField, schemaForms);
        fields.slice(1).forEach(f => {
          updatedSchemaForms = updateSchemaForms(f, updatedSchemaForms);
        });
        dispatch(updateSchemaModalState({ schemaForms: updatedSchemaForms }));
      }
    }
  };

const updateSchemaForm =
  (schemaForm: Schema): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const schemaForms = getState().schemaModal.schemaForms || [];
    dispatch(
      updateSchemaModalState({
        schemaForms: schemaForms.reduce(
          (sfs, sf) => {
            if (sf.props.itemType != schemaForm.props.itemType) {
              sfs.push(sf);
            }
            return sfs;
          },
          [{ ...schemaForm }]
        )
      })
    );
  };

const updateFields =
  (fields: SchemaField[]): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    dispatch(updateSchemaModalState({ fields }));
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  const { updateUrl } = URLManager.useRouter();
  return {
    openSchemaModal: (v?: Schema) => dispatch(openSchemaModal(v)),
    closeSchemaModal: () => dispatch(closeSchemaModal()),
    deleteSchema: async (v: Schema) => await dispatch(deleteSchema(v)),
    setSchemaSetting: (vs: VS) => dispatch(setSchemaSetting(vs)),
    removeField: (f: string) => dispatch(removeField(f)),
    updateField: (f: string, fi: FieldInfo, form?: Schema) =>
      dispatch(updateField(f, fi, form)),
    selectField: (selectedField: string) =>
      dispatch(updateSchemaModalState({ selectedField })),
    goToPage: (p: string) => dispatch(goToPage(p)),
    nextPage: () => dispatch(goToPage(1)),
    prevPage: () => dispatch(goToPage(-1)),
    addField: () => dispatch(addField()),
    addItemField: (
      index: number,
      aboveOrBelow: 'above' | 'below',
      filteredFields: SchemaField[]
    ) => dispatch(addItemField(index, aboveOrBelow, filteredFields)),
    updateFields: (fields: SchemaField[]) => dispatch(updateFields(fields)),
    updateSchemaForm: (schemaForm: Schema) =>
      dispatch(updateSchemaForm(schemaForm)),
    handleShowInOnChange: (
      field: string,
      itemTypes: string[],
      treeFields?: SchemaField[]
    ) => dispatch(handleShowInOnChange(field, itemTypes, treeFields)),
    save: () => dispatch(save(updateUrl))
  };
};
